import { useState } from "react";
import { Badge, Box, Collapse, Flex, Icon, Text } from "@chakra-ui/react";
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import {
  ICcbVinculoAtivo,
  IElegibilidadeSeguroBadge,
} from "@/models/consignado/proposal-billings.model";
import { cpfFormatter } from "@/utils/cpfFormatter";
import { BrlCurrencyFormatter } from "@/utils/BrlCurrencyFormatter";
import { formatDateWithNoHours } from "@/utils/formatDateWithNoHours";
import { ELEGIBILIDADE_LABEL } from "../VinculosEncerradosRedesign/statusEncerrado";
import CcbDetalhe from "./CcbDetalhe";
import CopyButton from "./CopyButton";
import { renderCompetenciaAAAAMM } from "./renderMesCompetencia";

type Props = {
  ccb: ICcbVinculoAtivo;
  tamanhoDestaque?: "sm" | "md";
  // Só em Vínculos Encerrados — elegibilidade ao seguro já classificada pelo
  // backend (mesmo SQL do filtro), exibida como badge na linha.
  elegibilidadeSeguro?: IElegibilidadeSeguroBadge;
};

// Linha de uma CCB (nível 2 da listagem). O cabeçalho tem duas áreas irmãs:
// a identificação (um <button> que expande/colapsa o detalhe) e os badges +
// ações à direita — fora do botão, pra não aninhar <button> em <button>
// (HTML inválido, warning de validateDOMNesting e navegação por teclado
// quebrada). O "Reprocessar" (global, por período) fica no cabeçalho da tela,
// ao lado do título — não aqui. Os contadores "x de y" são badges, só
// leitura — não abrem nada.
//
// Largura: os badges de texto longo (elegibilidade, desligamento) têm `maxW`
// pra quebrar linha dentro deles — sem isso o grupo da direita cresce até o
// conteúdo e esmaga a identificação numa coluna de um caractere. Se mesmo
// assim não couber, o cabeçalho quebra e o grupo desce pra linha de baixo.
const BADGE_TEXTO_MAX_W = "24rem";
const contadorBadgeStyle = (scheme: "red" | "green" | "blue" | "gray") => ({
  bg: `${scheme}.500`,
  color: "white",
  borderRadius: "md",
  px: 3,
  py: 1,
  fontFamily: "Lexend Deca",
  fontSize: "sm",
  fontWeight: "normal",
  textTransform: "none" as const,
  textAlign: "center" as const,
});
export default function CcbRow({
  ccb,
  tamanhoDestaque = "sm",
  elegibilidadeSeguro,
}: Props) {
  const [expanded, setExpanded] = useState(false);

  // Contadores vêm prontos do backend — o front NÃO varre parcelas nem faz conta.
  const naoEscriturados = ccb.qtd_nao_escriturados;
  const naoRepassado = ccb.qtd_nao_repassado;

  return (
    <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" bg="white">
      <Flex
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={4}
        px={4}
        py={3}
      >
        {/* A área inteira expande/colapsa no clique, mas o <button> de verdade
            (teclado, aria-expanded) é só o chevron — os botões de copiar
            ficam dentro da área e não podem estar aninhados num <button>. */}
        <Flex
          onClick={() => setExpanded((prev) => !prev)}
          cursor="pointer"
          flex="1 1 16rem"
          minW={0}
          alignItems="center"
          gap={3}
          textAlign="left"
          borderRadius="md"
          _hover={{ bg: "gray.50" }}
        >
          <Box
            as="button"
            type="button"
            aria-expanded={expanded}
            aria-label={expanded ? "Recolher CCB" : "Expandir CCB"}
            display="flex"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              setExpanded((prev) => !prev);
            }}
          >
            <Icon as={expanded ? ChevronUpIcon : ChevronDownIcon} color="gray.500" />
          </Box>
          {/* Identificação em uma linha só: tomador · CPF · CCB · shorthash.
              `baseline` alinha o destaque (maior) com CCB/hash (fontSize sm);
              em tela estreita o wrap quebra por item, não por caractere.
              CPF copia só dígitos (cola direto em consultas e na BMP). */}
          <Flex minW={0} alignItems="baseline" flexWrap="wrap" columnGap={2} rowGap={1}>
            <Text
              fontSize={tamanhoDestaque}
              fontWeight="bold"
              color="gray.700"
              wordBreak="break-word"
            >
              {ccb.tomador_nome} · {cpfFormatter(ccb.tomador_documento)}
            </Text>
            <CopyButton value={String(ccb.tomador_documento ?? "").replace(/\D/g, "")} rotulo="CPF" />
            <Text fontSize="sm" color="gray.500">
              · CCB {ccb.numero_ccb}
            </Text>
            <CopyButton value={String(ccb.numero_ccb ?? "")} rotulo="CCB" />
            {ccb.proposta_shorthash && (
              <Text fontSize="sm" color="gray.400">
                · {ccb.proposta_shorthash}
              </Text>
            )}
          </Flex>
        </Flex>

        <Flex gap={6} alignItems="center" flexWrap="wrap" justifyContent="flex-end" ml="auto">
          {elegibilidadeSeguro && (
            <Badge
              colorScheme={elegibilidadeSeguro.elegibilidade_seguro ? "purple" : "gray"}
              borderRadius="md"
              display="flex"
              flexDirection="column"
              alignItems="center"
              minH="2rem"
              maxW={BADGE_TEXTO_MAX_W}
              px={3}
              py={1}
              fontFamily="Lexend Deca"
              fontSize="sm"
              fontWeight="normal"
              textTransform="none"
              textAlign="center"
              whiteSpace="normal"
            >
              <span>
                {elegibilidadeSeguro.elegibilidade_seguro
                  ? ELEGIBILIDADE_LABEL[elegibilidadeSeguro.elegibilidade_seguro]
                  : "Elegibilidade a classificar"}
              </span>
              {elegibilidadeSeguro.motivos_nao_elegibilidade.map((motivo) => (
                <span key={motivo}>{motivo}</span>
              ))}
              {elegibilidadeSeguro.valor_parcela != null && (
                <span>Parcela: {BrlCurrencyFormatter(elegibilidadeSeguro.valor_parcela)}</span>
              )}
              {elegibilidadeSeguro.dias_desde_desligamento != null && (
                <span>VE: {elegibilidadeSeguro.dias_desde_desligamento} dias</span>
              )}
            </Badge>
          )}
          {(ccb.termino_vinculo || ccb.motivo_desligamento) && (
            <Badge
              colorScheme="orange"
              borderRadius="md"
              display="flex"
              flexDirection="column"
              alignItems="center"
              minH="2rem"
              maxW={BADGE_TEXTO_MAX_W}
              px={3}
              py={1}
              fontFamily="Lexend Deca"
              fontSize="sm"
              fontWeight="normal"
              textTransform="none"
              textAlign="center"
              whiteSpace="normal"
            >
              <span>Desligamento</span>
              {ccb.termino_vinculo && (
                <span>{formatDateWithNoHours(String(ccb.termino_vinculo.data_desligamento))}</span>
              )}
              {ccb.motivo_desligamento && <span>{ccb.motivo_desligamento}</span>}
            </Badge>
          )}

          {/* Escriturados e repassados lado a lado — com a identificação em uma
              linha, a linha da CCB inteira cabe numa altura só. Se a largura
              apertar, o wrap desce um badge, não quebra o texto deles. */}
          <Flex direction="row" gap={2} flexShrink={0} flexWrap="wrap" justifyContent="flex-end">
            {/* Competência em atraso mais antiga (backend) — a que ordena a
                listagem em "Competência em atraso" (TAR-ADM-cobranca-087). */}
            {ccb.competencia_atraso_mais_antiga != null && (
              <Badge {...contadorBadgeStyle("red")}>
                Atraso desde {renderCompetenciaAAAAMM(ccb.competencia_atraso_mais_antiga)}
              </Badge>
            )}
            <Badge {...contadorBadgeStyle(naoEscriturados > 0 ? "red" : "green")}>
              {naoEscriturados} {naoEscriturados === 1 ? "não escriturado" : "não escriturados"}
            </Badge>
            <Badge {...contadorBadgeStyle(naoRepassado > 0 ? "red" : "green")}>
              {naoRepassado} {naoRepassado === 1 ? "não repassado" : "não repassados"}
            </Badge>
            {/* Flag pronta do backend; sem o campo (ex.: Encerrados) não mostra. */}
            {ccb.tem_negociacao != null && (
              <Badge {...contadorBadgeStyle(ccb.tem_negociacao ? "blue" : "gray")}>
                {ccb.tem_negociacao ? "Com negociação" : "Sem negociação"}
              </Badge>
            )}
          </Flex>
        </Flex>
      </Flex>

      <Collapse in={expanded} animateOpacity>
        <Box px={4} pt={6} pb={4}>
          {expanded && (
            <CcbDetalhe ccb={ccb} />
          )}
        </Box>
      </Collapse>
    </Box>
  );
}
